import { useCallback } from "react";
import { useParams } from "react-router-dom";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import EditableTool, { EditableToolProps } from "./EditableTool";
import type { Tool, ToolPayload } from "./EditableSetup";
import { displayOpen, displayClose, remoteClose } from "src/pods/realtime";
import {
  useToolsByType,
  useCreateToolMutation,
  useEditToolMutation,
  useDeleteToolMutation,
} from "src/pods/toolkitData/useToolkitDataFetchers";
import { dispatchToolkitReduxAction } from "src/state/toolkitReduxStore";

type ToolType = "pairShare" | "today" | "directions";

type EditableToolContainerProps = Omit<
  EditableToolProps,
  | "displayOpen"
  | "displayClose"
  | "remoteClose"
  | "createTool"
  | "editTool"
  | "deleteTool"
  | "tools"
  | "classId"
  | "mountPoint"
>;

export default function EditableToolContainer(props: EditableToolContainerProps) {
  const { classId, mountPoint } = useParams<{ classId: string; mountPoint: string }>();
  const { data: tools } = useToolsByType(classId ?? "", props.toolType, { enabled: !!classId });
  const { mutate: createToolMutate } = useCreateToolMutation();
  const { mutate: editToolMutate } = useEditToolMutation();
  const { mutate: deleteToolMutate } = useDeleteToolMutation();

  const handleDisplayOpen = useCallback(
    (path: string, displayPayload?: ToolkitAction) => dispatchToolkitReduxAction(displayOpen(path, displayPayload)),
    [],
  );
  const handleDisplayClose = useCallback(() => dispatchToolkitReduxAction(displayClose()), []);
  const handleRemoteClose = useCallback(() => dispatchToolkitReduxAction(remoteClose()), []);

  if (!classId || !mountPoint) return null;

  return (
    <EditableTool
      {...props}
      classId={classId}
      mountPoint={mountPoint}
      tools={(tools as Tool[]) ?? []}
      displayOpen={handleDisplayOpen}
      displayClose={handleDisplayClose}
      remoteClose={handleRemoteClose}
      createTool={(data: { classId: string; type: string; payload: ToolPayload; dojoId?: string }) => {
        createToolMutate({
          path: { classId: data.classId },
          body: { type: data.type as ToolType, payload: data.payload, dojoId: data.dojoId },
        });
      }}
      editTool={(data: { classId: string; _id: string; payload: ToolPayload; updatedAt: string }) => {
        editToolMutate({
          path: { classId: data.classId, id: data._id },
          body: { payload: data.payload },
        });
      }}
      deleteTool={(data: { _id: string; classId: string }) => {
        deleteToolMutate({
          path: { classId: data.classId, id: data._id },
        });
      }}
    />
  );
}
